import { clamp, preventDefault } from './utilities.js';
import { ZoomDialog } from './zoom-dialog.js';

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
const DEFAULT_ZOOM = 1.5;
const DOUBLE_TAP_DELAY = 300;
const DOUBLE_TAP_DISTANCE = 50;
const DRAG_THRESHOLD = 10;

/** Tuned so one mouse-wheel notch (~100px of deltaY) is roughly a 15% zoom step. */
const WHEEL_ZOOM_SENSITIVITY = 0.0015;

/**
 * Where a click jumps to. Separate from DEFAULT_ZOOM (the touch double-tap level) because the image
 * starts fitted to the frame, so this has to be enough to actually read detail. Turn it up if
 * merchants want a closer look on one click; the wheel covers the rest of the range.
 */
const CLICK_ZOOM = 2.5;

export class DragZoomWrapper extends HTMLElement {
  #controller = new AbortController();
  /** The dialog always opens showing the whole image; zooming in is an explicit action. */
  #scale = MIN_ZOOM;
  /** @type {number} */
  #initialDistance = 0;
  /** @type {number} */
  #startScale = MIN_ZOOM;
  /** @type {Point} */
  #translate = { x: 0, y: 0 };
  /** @type {Point} */
  #startPosition = { x: 0, y: 0 };
  /** @type {Point} */
  #startTranslate = { x: 0, y: 0 };
  /** @type {boolean} */
  #isDragging = false;
  /** @type {boolean} */
  #initialized = false;
  /** @type {number | null} */
  #animationFrame = null;
  /** @type {number} */
  #lastTapTime = 0;
  /** @type {Point | null} */
  #lastTapPosition = null;

  /** @type {boolean} */
  #hasDraggedBeyondThreshold = false;

  /** @type {boolean} */
  #hasManualZoom = false;

  get #image() {
    return this.querySelector('img');
  }

  connectedCallback() {
    if (!this.#image) return;

    this.#initResizeListener();
    this.#setupDialogCloseListener();
    this.#initEventListeners();
    this.#updateTransform();
  }

  #initResizeListener() {
    this.#resizeObserver.observe(this);
  }

  /**
   * Override parent zoom dialog's close method to include reset functionality
   */
  #setupDialogCloseListener() {
    // Find the parent zoom dialog component
    const zoomDialog = /** @type {ZoomDialog} */ (this.closest('zoom-dialog'));
    if (!zoomDialog || typeof zoomDialog.close !== 'function') return;

    // Store reference to original close method
    const originalClose = zoomDialog.close.bind(zoomDialog);

    // Override the close method to include zoom reset
    zoomDialog.close = async (...args) => {
      // Reset zoom state before closing
      this.#resetZoom();

      // Call original close method
      return await originalClose(...args);
    };
  }

  #initEventListeners() {
    if (this.#initialized) return;
    this.#initialized = true;
    const { signal } = this.#controller;
    const options = { passive: false, signal };

    this.addEventListener('touchstart', this.#handleTouchStart, options);
    this.addEventListener('touchmove', this.#handleTouchMove, options);
    this.addEventListener('touchend', this.#handleTouchEnd, options);

    // Pointer devices get click-to-zoom with hover-panning, plus wheel for finer control.
    // `passive: false` on wheel is required to cancel page scrolling.
    this.addEventListener('wheel', this.#handleWheel, options);
    this.addEventListener('click', this.#handleClick, { signal });
    this.addEventListener('pointermove', this.#handlePointerMove, { signal });

    // Initialize transform immediately
    this.#updateTransform();
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#resizeObserver.disconnect();
    this.#cancelAnimationFrame();
  }

  #handleResize = () => {
    // The fitted size changes with the viewport, so the pan/zoom has to be re-clamped against the
    // new box.
    this.#requestUpdateTransform();
  };

  #resizeObserver = new ResizeObserver(this.#handleResize);

  /**
   * @param {TouchEvent} event
   */
  #handleTouchStart = (event) => {
    preventDefault(event);

    const touchCount = event.touches.length;

    if (touchCount === 2) {
      // Early exit if touches are invalid
      const touch1 = event.touches[0];
      const touch2 = event.touches[1];
      if (!touch1 || !touch2) return;

      // Avoid object allocation by passing touches directly
      this.#startZoomGestureFromTouches(touch1, touch2);
    } else if (touchCount === 1) {
      const touch = event.touches[0];
      if (!touch) return;

      // Use performance.now() for better precision and performance
      const currentTime = performance.now();
      const timeSinceLastTap = currentTime - this.#lastTapTime;

      // Early exit if too much time has passed
      if (timeSinceLastTap >= DOUBLE_TAP_DELAY) {
        this.#storeTapInfo(currentTime, touch);
        this.#startDragGestureFrom(touch);
        return;
      }

      // Only check distance if we have a previous tap within time window
      if (this.#lastTapPosition) {
        // Distance calculation with early exit
        const distance = getDistance(touch, this.#lastTapPosition);

        if (distance < DOUBLE_TAP_DISTANCE) {
          // This is a double-tap, handle zoom toggle
          this.#handleDoubleTapFromTouch(touch);
          this.#lastTapTime = 0; // Reset to prevent triple-tap
          this.#lastTapPosition = null;
          return;
        }
      }

      // Store tap info for potential double-tap detection
      this.#storeTapInfo(currentTime, touch);
      this.#startDragGestureFrom(touch);
    }
  };

  /**
   * Scales to `targetScale` while keeping the given viewport point stationary, which is what makes
   * zoom feel anchored to the cursor / pinch midpoint rather than to the image centre.
   *
   * @param {number} clientX - Viewport x of the anchor point.
   * @param {number} clientY - Viewport y of the anchor point.
   * @param {number} targetScale - Desired scale, clamped to the zoom range.
   */
  #zoomAbout(clientX, clientY, targetScale) {
    const oldScale = this.#scale;
    this.#scale = clamp(targetScale, MIN_ZOOM, MAX_ZOOM);

    // Measured rather than derived from clientWidth/Height: the wrapper no longer fills the
    // viewport, so its centre isn't half the viewport.
    const rect = this.getBoundingClientRect();
    const distanceFromCenterX = clientX - (rect.left + rect.width / 2);
    const distanceFromCenterY = clientY - (rect.top + rect.height / 2);

    const scaleDelta = this.#scale / oldScale - 1.0;
    this.#translate.x -= (distanceFromCenterX * scaleDelta) / this.#scale;
    this.#translate.y -= (distanceFromCenterY * scaleDelta) / this.#scale;

    this.#requestUpdateTransform();
  }

  /**
   * Wheel / trackpad zoom. Exponential so each notch is a constant proportion rather than a
   * constant amount, which keeps the step from feeling huge near 1x and tiny near 5x.
   *
   * @param {WheelEvent} event
   */
  #handleWheel = (event) => {
    preventDefault(event);

    this.#scale = clamp(this.#scale * Math.exp(-event.deltaY * WHEEL_ZOOM_SENSITIVITY), MIN_ZOOM, MAX_ZOOM);
    this.#hasManualZoom = this.#scale > MIN_ZOOM;

    // Reposition through the same cursor mapping as hover-panning rather than #zoomAbout's
    // incremental anchoring — otherwise the image jumps on the next mouse move.
    this.#panToPointer(event);
  };

  /**
   * Positions the zoomed image from the pointer: where the cursor sits over the wrapper maps onto
   * the same relative position of the image, so moving the mouse sweeps the visible area edge to
   * edge without any dragging.
   *
   * @param {MouseEvent} event - Any pointer, click, or wheel event over the wrapper.
   */
  #panToPointer(event) {
    const rect = this.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    const max = this.#getMaxTranslate();
    const fractionX = clamp((event.clientX - rect.left) / rect.width, 0, 1);
    const fractionY = clamp((event.clientY - rect.top) / rect.height, 0, 1);

    // Cursor at the left edge (0) shows the image's left edge, which needs a positive translation
    this.#translate.x = (0.5 - fractionX) * 2 * max.x;
    this.#translate.y = (0.5 - fractionY) * 2 * max.y;

    this.#requestUpdateTransform();
  }

  /**
   * @param {PointerEvent} event
   */
  #handlePointerMove = (event) => {
    // Touch pans by dragging through the touch handlers; hover-panning would fight that gesture.
    if (event.pointerType === 'touch' || this.#scale <= MIN_ZOOM) return;

    this.#panToPointer(event);
  };

  /**
   * Toggles between contained and zoomed, anchored wherever the shopper clicked. The dialog only
   * closes from its close button now, so a click on the media is free to mean "zoom here".
   *
   * @param {PointerEvent} event
   */
  #handleClick = (event) => {
    if (event.pointerType === 'touch') return;

    const zoomingIn = this.#scale <= MIN_ZOOM;

    this.#scale = zoomingIn ? CLICK_ZOOM : MIN_ZOOM;
    this.#hasManualZoom = zoomingIn;

    if (zoomingIn) return this.#panToPointer(event);

    this.#translate = { x: 0, y: 0 };
    this.#requestUpdateTransform();
  };

  /**
   * Start a zoom gesture with two touches
   * @param {Touch} touch1
   * @param {Touch} touch2
   */
  #startZoomGestureFromTouches(touch1, touch2) {
    // Calculate initial distance between touches
    this.#initialDistance = getDistance(touch1, touch2);
    this.#startScale = this.#scale;
    this.#isDragging = false;
  }

  /**
   * Start a drag gesture from a single touch
   * @param {Touch} touch
   */
  #startDragGestureFrom(touch) {
    this.#startPosition = { x: touch.clientX, y: touch.clientY };
    this.#startTranslate = { x: this.#translate.x, y: this.#translate.y };
    this.#isDragging = true;
    this.#hasDraggedBeyondThreshold = false;
  }

  /**
   * Store tap information for double-tap detection
   * @param {number} currentTime
   * @param {Touch} touch
   */
  #storeTapInfo(currentTime, touch) {
    this.#lastTapTime = currentTime;
    this.#lastTapPosition = { x: touch.clientX, y: touch.clientY };
  }

  /**
   * Handle double-tap zoom toggle from touch
   * @param {Touch} touch - The touch where the double-tap occurred
   */
  #handleDoubleTapFromTouch(touch) {
    let targetZoom;

    // If manual zoom has been used, reset to 1x
    if (this.#hasManualZoom) {
      targetZoom = MIN_ZOOM; // 1x
      this.#hasManualZoom = false; // Reset the flag
      this.#translate = { x: 0, y: 0 }; // Center the image
    } else {
      // Toggle between zoom levels: 1x ↔ 1.5x
      const tolerance = 0.05; // Small tolerance for floating point comparison

      if (Math.abs(this.#scale - MIN_ZOOM) < tolerance) {
        // Currently at 1x, go to 1.5x
        targetZoom = DEFAULT_ZOOM;
      } else {
        // Currently at 1.5x or any other level, go to 1x
        targetZoom = MIN_ZOOM;
      }
    }

    // If we're not going to 1x, adjust translation to center zoom on the tap point
    if (targetZoom !== MIN_ZOOM) {
      this.#zoomAbout(touch.clientX, touch.clientY, targetZoom);
      return;
    }

    // Going to 1x, set the scale and center the image
    this.#scale = targetZoom;
    this.#translate = { x: 0, y: 0 }; // Center the image when going to 1x

    this.#requestUpdateTransform();
  }

  /**
   * @param {TouchEvent} event
   */
  #handleTouchMove = (event) => {
    preventDefault(event);

    const touchCount = event.touches.length;

    if (touchCount === 2) {
      const touch1 = event.touches[0];
      const touch2 = event.touches[1];
      if (touch1 && touch2) {
        this.#processZoomGesture(touch1, touch2);
      }
    } else if (touchCount === 1 && this.#isDragging) {
      const touch = event.touches[0];
      if (touch) {
        this.#processDragGesture(touch);
      }
    }
  };

  /**
   * Process zoom gesture from touches
   * @param {Touch} touch1
   * @param {Touch} touch2
   */
  #processZoomGesture(touch1, touch2) {
    // Calculate midpoint directly without object allocation
    const midX = (touch1.clientX + touch2.clientX) / 2;
    const midY = (touch1.clientY + touch2.clientY) / 2;

    // Calculate current distance between touches
    const currentDistance = getDistance(touch1, touch2);

    // Mark that manual zoom has been used
    this.#hasManualZoom = true;

    // Zoom about the pinch midpoint so it stays under the shopper's fingers
    this.#zoomAbout(midX, midY, (currentDistance / this.#initialDistance) * this.#startScale);
    this.#isDragging = false;
  }

  /**
   * Process drag gesture from a touch
   * @param {Touch} touch
   */
  #processDragGesture(touch) {
    // Check if we've moved beyond the drag threshold
    const distance = getDistance(touch, this.#startPosition);

    if (!this.#hasDraggedBeyondThreshold && distance < DRAG_THRESHOLD) {
      // Movement is too small, don't process as drag yet
      return;
    }

    this.#hasDraggedBeyondThreshold = true;

    // Calculate movement deltas for translation
    const dx = touch.clientX - this.#startPosition.x;
    const dy = touch.clientY - this.#startPosition.y;

    // Calculate new translation directly
    this.#translate.x = this.#startTranslate.x + dx / this.#scale;
    this.#translate.y = this.#startTranslate.y + dy / this.#scale;

    this.#requestUpdateTransform();
  }

  /**
   * @param {TouchEvent} event
   */
  #handleTouchEnd = (event) => {
    if (event.touches.length === 0) {
      this.#isDragging = false;
      this.#requestUpdateTransform();

      this.#hasDraggedBeyondThreshold = false;
    }
  };

  /**
   * How far the image can travel in each axis before its edge would pull inside the wrapper. The
   * image starts centred, so that's half the overflow — divided by the scale because the CSS
   * `scale() translate()` order means translation is applied in pre-scale units.
   *
   * @returns {Point} Max translation per axis, `{ x: 0, y: 0 }` when nothing overflows.
   */
  #getMaxTranslate() {
    const wrapperRect = this.getBoundingClientRect();
    const imageElement = this.#image;

    if (!wrapperRect.width || !wrapperRect.height || !imageElement) return { x: 0, y: 0 };

    // The image element fills the wrapper, but `object-fit: contain` means the visible content is
    // letterboxed to its own aspect ratio — that content box is what can overflow.
    let naturalWidth, naturalHeight;

    if (imageElement.naturalWidth > 0 && imageElement.naturalHeight > 0) {
      naturalWidth = imageElement.naturalWidth;
      naturalHeight = imageElement.naturalHeight;
    } else {
      // Fallback: assume square image if we can't get natural dimensions
      naturalWidth = wrapperRect.width;
      naturalHeight = wrapperRect.width;
    }

    const imageAspectRatio = naturalWidth / naturalHeight;
    const wrapperAspectRatio = wrapperRect.width / wrapperRect.height;

    let actualImageWidth, actualImageHeight;

    if (imageAspectRatio > wrapperAspectRatio) {
      // Image is wider - width fits exactly, height is smaller (letterboxed top/bottom)
      actualImageWidth = wrapperRect.width;
      actualImageHeight = actualImageWidth / imageAspectRatio;
    } else {
      // Image is taller - height fits exactly, width is smaller (letterboxed left/right)
      actualImageHeight = wrapperRect.height;
      actualImageWidth = actualImageHeight * imageAspectRatio;
    }

    const horizontalOverflow = Math.max(0, actualImageWidth * this.#scale - wrapperRect.width);
    const verticalOverflow = Math.max(0, actualImageHeight * this.#scale - wrapperRect.height);

    return {
      x: horizontalOverflow / 2 / this.#scale,
      y: verticalOverflow / 2 / this.#scale,
    };
  }

  /**
   * Constrain image translation to keep it within the viewport
   */
  #constrainTranslation() {
    if (!this.clientWidth || !this.clientHeight || !this.#image) return;

    // Keep scale between MIN_ZOOM (1) and MAX_ZOOM (5)
    this.#scale = clamp(this.#scale, MIN_ZOOM, MAX_ZOOM);

    // At minimum zoom (1x), the full image should be visible with no dragging allowed
    if (this.#scale <= MIN_ZOOM) {
      this.#translate.x = 0;
      this.#translate.y = 0;
      return;
    }

    // Apply symmetric constraints (object-fit: contain behavior)
    // Image starts centered, can move maxTranslate in each direction
    const max = this.#getMaxTranslate();

    this.#translate.x = clamp(this.#translate.x, -max.x, max.x);
    this.#translate.y = clamp(this.#translate.y, -max.y, max.y);
  }

  /**
   * Request an animation frame to update the transform
   */
  #requestUpdateTransform = () => {
    if (!this.#animationFrame) {
      this.#animationFrame = requestAnimationFrame(this.#updateTransform);
    }
  };

  /**
   * Cancel any pending animation frame
   */
  #cancelAnimationFrame() {
    if (this.#animationFrame) {
      cancelAnimationFrame(this.#animationFrame);
      this.#animationFrame = null;
    }
  }

  #updateTransform = () => {
    this.#animationFrame = null;

    this.#constrainTranslation();

    // Drives the pan cursor — CSS can't compare --drag-zoom-scale against 1
    this.toggleAttribute('data-zoomed', this.#scale > MIN_ZOOM);
    this.style.setProperty('--drag-zoom-scale', this.#scale.toString());
    this.style.setProperty('--drag-zoom-translate-x', `${this.#translate.x}px`);
    this.style.setProperty('--drag-zoom-translate-y', `${this.#translate.y}px`);
  };

  /**
   * Reset zoom to default state (1.5x scale, centered position)
   * Called when zoom is exited/closed
   */
  #resetZoom() {
    // Reset scale and translation to defaults
    this.#scale = MIN_ZOOM;
    this.#startScale = MIN_ZOOM;
    this.#translate.x = 0;
    this.#translate.y = 0;

    // Reset gesture state to prevent interference on next zoom open
    this.#startPosition = { x: 0, y: 0 };
    this.#startTranslate = { x: 0, y: 0 };
    this.#isDragging = false;
    this.#lastTapTime = 0;
    this.#lastTapPosition = null;
    this.#hasDraggedBeyondThreshold = false;

    // Without this a reopened dialog still counts as manually zoomed, so the first double-tap
    // would reset to 1x instead of zooming in, and a click wouldn't close.
    this.#hasManualZoom = false;

    // Update CSS properties to reflect reset state
    this.toggleAttribute('data-zoomed', false);
    this.style.setProperty('--drag-zoom-scale', MIN_ZOOM.toString());
    this.style.setProperty('--drag-zoom-translate-x', '0px');
    this.style.setProperty('--drag-zoom-translate-y', '0px');
  }

  destroy() {
    this.#controller.abort();
    this.#cancelAnimationFrame();
  }
}

/**
 * Calculate distance between two points or touches
 * @param {Point | Touch} point1 - First point or touch
 * @param {Point | Touch} point2 - Second point or touch
 * @returns {number} Distance between the points
 */
function getDistance(point1, point2) {
  // Handle both Point objects (x, y) and Touch objects (clientX, clientY)
  const x1 = /** @type {Point} */ (point1).x ?? /** @type {Touch} */ (point1).clientX;
  const y1 = /** @type {Point} */ (point1).y ?? /** @type {Touch} */ (point1).clientY;
  const x2 = /** @type {Point} */ (point2).x ?? /** @type {Touch} */ (point2).clientX;
  const y2 = /** @type {Point} */ (point2).y ?? /** @type {Touch} */ (point2).clientY;

  const dx = x1 - x2;
  const dy = y1 - y2;
  return Math.sqrt(dx * dx + dy * dy);
}

if (!customElements.get('drag-zoom-wrapper')) {
  customElements.define('drag-zoom-wrapper', DragZoomWrapper);
}

/**
 * @typedef {Object} Point
 * @property {number} x
 * @property {number} y
 */

/**
 * @typedef {HTMLElement} ZoomDialogElement
 * @property {Function} close - Method to close the zoom dialog
 */
